import Component from '@glimmer/component';
import Chip from 'tio-ui/components/chip';
import Select from 'tio-ui/components/select';
import Input from 'tio-ui/components/input';
import Flatpickr from 'tio-common/components/flatpickr';
import { PhX, PhDotsSixVertical } from 'ember-phosphor-icons';
import type { FieldOption } from '../../utils/opensearch-field-mapper';
import type { FilterOperator } from '../../utils/filter-operators';

/**
 * Private renderer for one filter row inside ActiveFiltersZone.
 * Takes the parent component as `@parent` so it can reuse its methods
 * (operator detection, value helpers, type predicates) without
 * duplicating ~25 props. FilterRow is not intended for use elsewhere.
 */
export interface FilterRowSignature {
  Args: {
    field: FieldOption;
    currentOp: FilterOperator;
    typeConfig: {
      intent: 'default' | 'warning' | 'success' | 'primary' | 'danger';
      label: string;
    };
    // Reference back to the parent component so we can call its methods.
    // Typed loosely to avoid a circular import — the methods we call are
    // documented in ActiveFiltersZone.
    parent: {
      getOperatorItems: (field: FieldOption) => Array<{
        key: FilterOperator;
        label: string;
        description?: string;
      }>;
      handleOperatorChange: (fieldKey: string, newOp: string) => void;
      handleValueForOperator: (fieldKey: string, op: FilterOperator, value: string) => void;
      handleNumberForOperator: (fieldKey: string, op: FilterOperator, value: string) => void;
      handleDateValueForOperator: (
        fieldKey: string,
        op: FilterOperator,
        dates: Date[],
        dateStr: string
      ) => void;
      handleDateStartChange: (fieldKey: string, dates: Date[], dateStr: string) => void;
      handleDateEndChange: (fieldKey: string, dates: Date[], dateStr: string) => void;
      handleNumberMinChange: (fieldKey: string, value: string) => void;
      handleNumberMaxChange: (fieldKey: string, value: string) => void;
      handleBooleanChange: (fieldKey: string, value: string) => void;
      handleMultiSelectChange: (fieldKey: string, op: FilterOperator, keys: string[]) => void;
      handleRelativeDateChange: (fieldKey: string, value: string) => void;
      getValueForOperator: (field: FieldOption) => string;
      getFilterValue: (key: string) => string | null | undefined;
      getFilterRangeStart: (key: string) => string;
      getFilterRangeEnd: (key: string) => string;
      getMultiSelectValues: (field: FieldOption) => string[];
      getDistinctItems: (field: FieldOption) => Array<{ key: string; label: string }>;
      getEnumItems: (field: FieldOption) => Array<{ key: string; label: string }>;
      /** Enum items: fetched distinct set (incl. empty) or the full enum fallback. */
      getEnumFilterItems: (field: FieldOption) => Array<{ key: string; label: string }>;
      hasDistinctValues: (field: FieldOption) => boolean | undefined;
      hasEnumValues: (field: FieldOption) => boolean | undefined;
      isDateType: (field: FieldOption) => boolean | undefined;
      isNumericType: (field: FieldOption) => boolean | undefined;
      isBooleanType: (field: FieldOption) => boolean | undefined;
      isTextType: (field: FieldOption) => boolean | undefined;
      isMultiSelectOp: (op: FilterOperator) => boolean;
      isBetweenOp: (op: FilterOperator) => boolean;
      isNoValueOperator: (op: FilterOperator) => boolean;
      isEmptyOp: (op: FilterOperator) => boolean;
      isRelativeDateOp: (op: FilterOperator) => boolean;
      relativeDateItems: Array<{ key: string; label: string }>;
      /** Build-filter date window: clamp bounds + overflow warning (no-ops outside view mode). */
      dateWindowMin: (field: FieldOption) => string;
      dateWindowMax: (field: FieldOption) => string;
      dateRangeEndMin: (field: FieldOption) => string;
      dateWindowWarning: (field: FieldOption) => string;
    };
    /** Omit to hide the remove ✕ (viewers adjusting a fixed filter set). */
    onRemove?: (field: FieldOption) => void;
    /** Optional "move filter into / out of group" targets */
    moveTargets?: Array<{ key: string; label: string }>;
    onMoveTargetChange?: (fieldKey: string, target: string | null) => void;
  };
  Element: HTMLDivElement;
}

class FilterRow extends Component<FilterRowSignature> {
  get hasMoveTargets(): boolean {
    return !!this.args.moveTargets && this.args.moveTargets.length > 0;
  }

  get moveTargetItems(): Array<{ key: string; label: string }> {
    return this.args.moveTargets ?? [];
  }

  handleMoveChange = (value: string | null) => {
    this.args.onMoveTargetChange?.(this.args.field.key, value);
  };

  <template>
    <div class="border tio-border-default rounded-md p-3 tio-bg-sunken" ...attributes>
      <div class="tio-row-between mb-2">
        <div class="tio-row-tight">
          <PhDotsSixVertical @size={{14}} class="tio-fg-muted" />
          <span class="tio-text-sm-medium tio-fg-secondary">
            {{@field.label}}
          </span>
          <Chip @size="sm" @intent={{@typeConfig.intent}} @appearance="faded">
            {{@typeConfig.label}}
          </Chip>
        </div>
        <div class="tio-row-tight">
          {{#if this.hasMoveTargets}}
            <Select
              @items={{this.moveTargetItems}}
              @selected=""
              @onChange={{this.handleMoveChange}}
              @placeholder="Group…"
              @size="sm"
              @clearable={{false}}
              @searchable={{false}}
            />
          {{/if}}
          {{#if @onRemove}}
            <button
              type="button"
              class="p-0.5 tio-fg-muted hover:tio-fg-secondary"
              aria-label="Remove {{@field.label}} filter"
              title="Remove filter"
              {{on "click" (fn @onRemove @field)}}
            >
              <PhX @size={{14}} />
            </button>
          {{/if}}
        </div>
      </div>

      <div class="mb-2">
        <Select
          @items={{@parent.getOperatorItems @field}}
          @selected={{@currentOp}}
          @onChange={{fn @parent.handleOperatorChange @field.key}}
          @size="sm"
          @searchable={{false}}
          @clearable={{false}}
        />
      </div>

      {{#if (@parent.isNoValueOperator @currentOp)}}
        <p class="text-xs tio-fg-muted italic">
          {{if
            (@parent.isEmptyOp @currentOp)
            "Matches records where this field has no value."
            "Matches records where this field has a value."
          }}
        </p>
      {{else if (@parent.isRelativeDateOp @currentOp)}}
        <Select
          @items={{@parent.relativeDateItems}}
          @selected={{@parent.getValueForOperator @field}}
          @onChange={{fn @parent.handleRelativeDateChange @field.key}}
          @placeholder="Select a time range…"
          @size="sm"
          @clearable={{true}}
          @searchable={{true}}
        />
      {{else if (@parent.isBetweenOp @currentOp)}}
        {{#if (@parent.isDateType @field)}}
          <div class="tio-row-tight">
            <div class="flex-1 relative">
              <Flatpickr
                @date={{if
                  (@parent.getFilterRangeStart @field.key)
                  (@parent.getFilterRangeStart @field.key)
                  ""
                }}
                @onChange={{fn @parent.handleDateStartChange @field.key}}
                @dateFormat="Y-m-d"
                @altInput={{true}}
                @altFormat="M j, Y"
                @minDate={{@parent.dateWindowMin @field}}
                @maxDate={{@parent.dateWindowMax @field}}
                placeholder="Start date"
                class="tio-form-input text-sm"
              />
            </div>
            <span class="tio-fg-muted">–</span>
            <div class="flex-1 relative">
              <Flatpickr
                @date={{if
                  (@parent.getFilterRangeEnd @field.key)
                  (@parent.getFilterRangeEnd @field.key)
                  ""
                }}
                @onChange={{fn @parent.handleDateEndChange @field.key}}
                @dateFormat="Y-m-d"
                @altInput={{true}}
                @altFormat="M j, Y"
                @minDate={{@parent.dateRangeEndMin @field}}
                @maxDate={{@parent.dateWindowMax @field}}
                placeholder="End date"
                class="tio-form-input text-sm"
              />
            </div>
          </div>
        {{else}}
          <div class="tio-row-tight">
            <div class="flex-1">
              <Input
                @type="number"
                @size="sm"
                @value={{@parent.getFilterRangeStart @field.key}}
                @onChange={{fn @parent.handleNumberMinChange @field.key}}
                @placeholder="Min"
              />
            </div>
            <span class="tio-fg-muted">–</span>
            <div class="flex-1">
              <Input
                @type="number"
                @size="sm"
                @value={{@parent.getFilterRangeEnd @field.key}}
                @onChange={{fn @parent.handleNumberMaxChange @field.key}}
                @placeholder="Max"
              />
            </div>
          </div>
        {{/if}}
      {{else if (@parent.isMultiSelectOp @currentOp)}}
        {{#if (@parent.hasDistinctValues @field)}}
          <Select
            @items={{@parent.getDistinctItems @field}}
            @multiple={{true}}
            @selectedKeys={{@parent.getMultiSelectValues @field}}
            @onChangeKeys={{fn @parent.handleMultiSelectChange @field.key @currentOp}}
            @placeholder="Select values…"
            @size="sm"
            @clearable={{true}}
            @searchable={{true}}
          />
        {{else if (@parent.hasEnumValues @field)}}
          <Select
            @items={{@parent.getEnumFilterItems @field}}
            @multiple={{true}}
            @selectedKeys={{@parent.getMultiSelectValues @field}}
            @onChangeKeys={{fn @parent.handleMultiSelectChange @field.key @currentOp}}
            @placeholder="Select values…"
            @size="sm"
            @clearable={{true}}
            @searchable={{true}}
          />
        {{else}}
          <Input
            @size="sm"
            @value={{@parent.getValueForOperator @field}}
            @onChange={{fn @parent.handleValueForOperator @field.key @currentOp}}
            @placeholder="Enter comma-separated values…"
          />
        {{/if}}
      {{else if (@parent.hasEnumValues @field)}}
        {{! Fetched distinct values reflect current filters; static enum options are only a fallback. }}
        <Select
          @items={{@parent.getEnumFilterItems @field}}
          @selected={{@parent.getValueForOperator @field}}
          @onChange={{fn @parent.handleValueForOperator @field.key @currentOp}}
          @placeholder="Select a value…"
          @size="sm"
          @clearable={{true}}
          @searchable={{true}}
        />
      {{else if (@parent.isBooleanType @field)}}
        <Select
          @items={{array (hash key="true" label="Yes") (hash key="false" label="No")}}
          @selected={{@parent.getFilterValue @field.key}}
          @onChange={{fn @parent.handleBooleanChange @field.key}}
          @placeholder="Any"
          @size="sm"
          @clearable={{true}}
        />
      {{else if (@parent.isDateType @field)}}
        <Flatpickr
          @date={{if (@parent.getValueForOperator @field) (@parent.getValueForOperator @field) ""}}
          @onChange={{fn @parent.handleDateValueForOperator @field.key @currentOp}}
          @dateFormat="Y-m-d"
          @altInput={{true}}
          @altFormat="M j, Y"
          @minDate={{@parent.dateWindowMin @field}}
          @maxDate={{@parent.dateWindowMax @field}}
          placeholder="Select a date"
          class="tio-form-input text-sm"
        />
      {{else if (@parent.isNumericType @field)}}
        <Input
          @type="number"
          @size="sm"
          @value={{@parent.getValueForOperator @field}}
          @onChange={{fn @parent.handleNumberForOperator @field.key @currentOp}}
          @placeholder="Enter a number…"
        />
      {{else if (@parent.isTextType @field)}}
        {{#if (@parent.hasDistinctValues @field)}}
          <Select
            @items={{@parent.getDistinctItems @field}}
            @selected={{@parent.getValueForOperator @field}}
            @onChange={{fn @parent.handleValueForOperator @field.key @currentOp}}
            @placeholder="Select value…"
            @size="sm"
            @clearable={{true}}
            @searchable={{true}}
          />
        {{else}}
          <Input
            @size="sm"
            @value={{@parent.getValueForOperator @field}}
            @onChange={{fn @parent.handleValueForOperator @field.key @currentOp}}
            @placeholder="Enter a value…"
          />
        {{/if}}
      {{else}}
        <Input
          @size="sm"
          @value={{@parent.getValueForOperator @field}}
          @onChange={{fn @parent.handleValueForOperator @field.key @currentOp}}
          @placeholder="Enter value…"
        />
      {{/if}}

      {{#let (@parent.dateWindowWarning @field) as |dateWarning|}}
        {{#if dateWarning}}
          <p
            class="tio-text-xs-medium tio-fg-warning mt-1"
            data-test-filter-row-date-window-warning
          >
            {{dateWarning}}
          </p>
        {{/if}}
      {{/let}}
    </div>
  </template>
}

export default FilterRow;
